import React from 'react';
import {
  Paper,
  Stack,
  TextField,
  Typography,
  Autocomplete,
  Checkbox,
} from 'components/mui';
import { DatePicker, LocalizationProvider, AdapterDateFns } from 'components/mui';
import { format, isValid } from 'date-fns';
import { SREDConsultant } from 'api/sred';
import { SREDParams } from '../types';
import { formatProjectLabel } from '../libs';

interface SREDFiltersProps {
  params: SREDParams;
  onParamsChange: (updates: Partial<SREDParams>) => void;
  availableConsultants: SREDConsultant[];
  availableProjects: string[];
}

function SREDFilters({ params, onParamsChange, availableConsultants, availableProjects }: SREDFiltersProps) {
  const handleStartDateChange = (date: Date | null) => {
    if (date && isValid(date)) {
      onParamsChange({ startDate: format(date, 'yyyy-MM-dd') });
    } else {
      onParamsChange({ startDate: null });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date && isValid(date)) {
      onParamsChange({ endDate: format(date, 'yyyy-MM-dd') });
    } else {
      onParamsChange({ endDate: null });
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onParamsChange({ sredYear: e.target.value });
  };

  const selectedConsultants = (availableConsultants || []).filter(c =>
    params.selectedConsultantIds.includes(c.contactId)
  );

  const selectedProjects = (availableProjects || []).filter(p =>
    params.selectedProjects.includes(p)
  );

  return (
    <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Filters
      </Typography>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <DatePicker
            label="Start Date"
            value={params.startDate ? new Date(params.startDate + 'T00:00:00') : null}
            onChange={handleStartDateChange}
            renderInput={(inputProps) => (
              <TextField {...inputProps} size="small" fullWidth sx={{ maxWidth: 220 }} />
            )}
          />
          <DatePicker
            label="End Date"
            value={params.endDate ? new Date(params.endDate + 'T00:00:00') : null}
            onChange={handleEndDateChange}
            renderInput={(inputProps) => (
              <TextField {...inputProps} size="small" fullWidth sx={{ maxWidth: 220 }} />
            )}
          />
          <TextField
            label="SRED Year"
            value={params.sredYear}
            onChange={handleYearChange}
            size="small"
            sx={{ maxWidth: 120 }}
          />
        </Stack>
      </LocalizationProvider>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        {availableConsultants.length > 0 && (
          <Autocomplete
            multiple
            size="small"
            options={availableConsultants}
            value={selectedConsultants}
            getOptionLabel={(option) => `${option.name} (${option.totalHours}h)`}
            isOptionEqualToValue={(option, val) => option.contactId === val.contactId}
            disableCloseOnSelect
            onChange={(_e, value) => {
              onParamsChange({ selectedConsultantIds: value.map(c => c.contactId) });
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                {option.name} ({option.totalHours}h)
              </li>
            )}
            renderInput={(inputProps) => (
              <TextField
                {...inputProps}
                label="Consultants"
                placeholder={selectedConsultants.length === 0 ? 'All' : ''}
              />
            )}
            sx={{ minWidth: 300, flex: 1 }}
          />
        )}

        {availableProjects.length > 0 && (
          <Autocomplete
            multiple
            size="small"
            options={availableProjects}
            value={selectedProjects}
            disableCloseOnSelect
            getOptionLabel={(option) => formatProjectLabel(option)}
            onChange={(_e, value) => {
              onParamsChange({ selectedProjects: value });
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
                {formatProjectLabel(option)}
              </li>
            )}
            renderInput={(inputProps) => (
              <TextField
                {...inputProps}
                label="Projects"
                placeholder={selectedProjects.length === 0 ? 'All' : ''}
              />
            )}
            sx={{ minWidth: 300, flex: 1 }}
          />
        )}
      </Stack>
    </Paper>
  );
}

export default SREDFilters;
